import type { AgentRecord } from 'src/app/core/services/agent-api.service';

/** Matches super-admin Prisma agent.status values. */
export type AgentLifecycleStatus = AgentRecord['status'];

const LIFECYCLE_STATUSES = new Set<AgentLifecycleStatus>(['DRAFT', 'DEPLOYED', 'ACTIVE', 'INACTIVE']);

export function normalizeAgentStatus(raw: string | undefined | null): AgentLifecycleStatus | null {
  const s = (raw ?? '').trim().toUpperCase() as AgentLifecycleStatus;
  if (LIFECYCLE_STATUSES.has(s)) {
    return s;
  }
  return null;
}

/** Map legacy / UI filter values to DB status. */
export function mapAgentListStatusFilter(raw: string | undefined): AgentLifecycleStatus | undefined {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return undefined;
  const direct = normalizeAgentStatus(trimmed);
  if (direct) return direct;
  const lower = trimmed.toLowerCase();
  if (lower === 'active') return 'ACTIVE';
  if (lower === 'inactive' || lower === 'paused') return 'INACTIVE';
  if (lower === 'deployed' || lower === 'pending') return 'DEPLOYED';
  if (lower === 'draft') return 'DRAFT';
  return undefined;
}

export function agentStatusDisplayLabel(status: string | undefined | null): string {
  switch (normalizeAgentStatus(status)) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Paused';
    case 'DEPLOYED':
      return 'Deployed';
    case 'DRAFT':
      return 'Draft';
    default:
      return status?.trim() ? status.trim() : '—';
  }
}

/** Live agent — can be paused. */
export function canPauseAgent(status: string | undefined | null): boolean {
  return normalizeAgentStatus(status) === 'ACTIVE';
}

/** Deployed (not live yet) or paused — can go live / resume. */
export function canActivateAgent(status: string | undefined | null): boolean {
  const s = normalizeAgentStatus(status);
  return s === 'INACTIVE' || s === 'DEPLOYED';
}

export type AgentLifecycleAction = 'pause' | 'activate';

export function agentLifecycleAction(status: string | undefined | null): AgentLifecycleAction | null {
  if (canPauseAgent(status)) return 'pause';
  if (canActivateAgent(status)) return 'activate';
  return null;
}

export function lifecycleMenuLabel(status: string | undefined | null): string {
  const action = agentLifecycleAction(status);
  if (action === 'pause') return 'Pause';
  if (action === 'activate') {
    return normalizeAgentStatus(status) === 'DEPLOYED' ? 'Go live' : 'Resume';
  }
  return '';
}

export function statusBadgeClassForAgent(status: string | undefined | null): Record<string, boolean> {
  const s = normalizeAgentStatus(status);
  return {
    'text-success bg-success-light': s === 'ACTIVE',
    'text-warning bg-warning-light': s === 'INACTIVE',
    'text-info bg-info-light': s === 'DEPLOYED',
    'text-secondary bg-secondary-light': s === 'DRAFT',
  };
}

export function lifecycleConfirmCopy(
  status: string | undefined | null,
  agentLabel: string,
): { title: string; message: string; confirmLabel: string; confirmColor: 'primary' | 'warn' } | null {
  const action = agentLifecycleAction(status);
  if (!action) return null;
  if (action === 'pause') {
    return {
      title: 'Pause agent?',
      message: `Pausing "${agentLabel}" stops it from handling new PD sessions. You can resume it later.`,
      confirmLabel: 'Pause',
      confirmColor: 'warn',
    };
  }
  if (normalizeAgentStatus(status) === 'DEPLOYED') {
    return {
      title: 'Go live?',
      message: `"${agentLabel}" is deployed but not live yet. Going live allows it to handle new PD sessions.`,
      confirmLabel: 'Go live',
      confirmColor: 'primary',
    };
  }
  return {
    title: 'Resume agent?',
    message: `Resume "${agentLabel}" so it can handle new PD sessions again.`,
    confirmLabel: 'Resume',
    confirmColor: 'primary',
  };
}
